'use client';

import React, { useEffect } from 'react';
import { cn } from '../utils/cn';

type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const sizeClasses: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-3xl',
  xl: 'max-w-6xl',
  '2xl': 'max-w-2xl',
};

type DialogProps = {
  isOpen: boolean;
  onClose: () => void;
  size?: DialogSize;
  /** Prevents Escape key and backdrop click from closing (e.g. while submitting) */
  disableClose?: boolean;
  /** Extra classes applied to the dialog box itself */
  className?: string;
  'aria-labelledby'?: string;
  children: React.ReactNode;
};

/**
 * Base dialog component with correct backdrop structure.
 *
 * The backdrop is a separate sibling div (not a parent) so it never creates a
 * backdrop-filter compositing context that would interfere with
 * glass-surface-static-solid-bg rendering.
 */
export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  size = 'md',
  disableClose = false,
  className,
  'aria-labelledby': ariaLabelledby,
  children,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !disableClose) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, disableClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in-0 duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledby}
    >
      {/* Backdrop — sibling, no backdrop-filter */}
      <div className="absolute inset-0 bg-black/50" onClick={disableClose ? undefined : onClose} />
      {/* Dialog box */}
      <div
        className={cn(
          'relative w-full',
          'rounded-2xl bg-background glass-surface-border shadow-2xl',
          'animate-in zoom-in-95 slide-in-from-bottom-4 duration-200',
          sizeClasses[size],
          className
        )}
      >
        {children}
      </div>
    </div>
  );
};
